/**
 * The catalogue drawer's filter core — pure, no I/O, heavily tested.
 *
 * Filters split in two because products-api genuinely divides in two:
 *
 * - **Wire** filters have a real query param and narrow the whole catalogue.
 * - **Sift** filters do not exist server-side. `clarity`, `colourGrade`, `labGrown` and `certified`
 *   are columns on the Stone table with no filter over them; `certificationLab` and `source` aren't
 *   filterable either. They can only be applied to the rows already loaded, so the UI says so
 *   rather than implying a catalogue-wide filter.
 *
 * Everything here exists to keep the SPA on the right side of products-api's sharp edges, all of
 * which were confirmed against the live local API:
 *
 * 1. **Arrays are ONE comma-separated param.** `?cut=ROUND&cut=OVAL` does not union — the route
 *    rebuilds `req.query` through `Object.fromEntries(searchParams)`, so duplicate keys collapse
 *    last-wins and it silently filters by OVAL alone (168 rows, exactly `cut=OVAL`). `?cut[]=…`
 *    is worse: the decoder calls `.split(",")` on an array and 500s.
 * 2. **`carat`/`price` split on `"-"`, not `","`** — `carat=0.5-2`, not `0.5,2` (which parses as
 *    min-only 0.5 and silently returns the wrong rows). A lone value is a minimum.
 * 3. **`metal` is OR'd with the other attribute filters; `defaultMetal` is AND'd.** Live proof:
 *    `cut=ROUND` → 46, `metal=PLATINUM` → 241, both together → 254 (a union — MORE than either).
 *    With `defaultMetal` it's 33. A metal control built on `metal` would be actively wrong, so the
 *    bench sends `defaultMetal`, as the production website does.
 * 4. **`cut`/`stones`/`colours` are cast into Prisma with no validation** — one unknown value is a
 *    500. Controls are driven from the mirrored enum lists in `enums.ts`, so that can't happen.
 * 5. **`diamondOrigin` is silently dropped** unless `stones` is empty or includes DIAMOND.
 */
import { RING_PRODUCT_TYPES } from "./enums";
import type { GemstoneSummary, RingDetail, Stone } from "./mapping";

export type SpecimenKind = "ring" | "stone";

/** Filters products-api can execute. Every field maps to a query param the API really reads. */
export interface WireFilters {
  search: string;
  /** Rings only. Empty → all five ring types (including both wedding bands). */
  productTypes: string[];
  cut: string[];
  /** Rings only — the singular `centerStone` param, which matches a CENTRE-role stone. */
  centreStone: string;
  /** Stones only — the `stones` array param, which matches a GEMSTONE-role (loose) stone. */
  stones: string[];
  /** Rings only. Sent as `defaultMetal` (AND'd), never `metal` (OR'd). */
  defaultMetal: string[];
  colours: string[];
  /** Range bounds as raw input strings; "" means unset. */
  caratMin: string;
  caratMax: string;
  priceMin: string;
  priceMax: string;
  audience: string[];
  /** "" omits the param. `ALL` is a server-side no-op, so it is treated the same as unset. */
  stockStatus: string;
  /** A ring with no photo can't be generated from, so this is a genuinely useful bench filter. */
  withImages: boolean;
  /** Stones only — merge live third-party (Nivoda) offers into the results. */
  includeNivoda: boolean;
  /** Stones only. "" | LAB_GROWN | NATURAL. Only reaches the API when `diamondOriginApplies`. */
  diamondOrigin: string;
}

/** Filters with no server-side equivalent — applied to the loaded rows in the browser. */
export interface SiftFilters {
  clarity: string[];
  colourGrade: string[];
  /** "" | "lab" | "natural" */
  labGrown: string;
  /** INTERNAL | NIVODA — decides which attributes a stone can even carry. */
  source: string[];
  certLab: string[];
  /** "" | "yes" | "no" */
  certified: string;
  /** Rings only. "" | "multi" — more than one photo, so `all_images` has something to fan out to. */
  photos: string;
  /** Rings only. "" | "clean" | "flagged" — whether the mapper could derive a clean baseline. */
  baseline: string;
}

export function emptyWire(kind: SpecimenKind): WireFilters {
  return {
    search: "",
    productTypes: [],
    cut: [],
    centreStone: "",
    stones: [],
    defaultMetal: [],
    colours: [],
    caratMin: "",
    caratMax: "",
    priceMin: "",
    priceMax: "",
    audience: [],
    stockStatus: "",
    withImages: false,
    includeNivoda: kind === "stone",
    diamondOrigin: "",
  };
}

export function emptySift(): SiftFilters {
  return { clarity: [], colourGrade: [], labGrown: "", source: [], certLab: [], certified: "", photos: "", baseline: "" };
}

/**
 * `diamondOrigin` is dropped by products-api unless the query is unconstrained by stone type or
 * explicitly includes diamonds. Exported so the control can disable itself and explain why, rather
 * than looking active while doing nothing.
 */
export function diamondOriginApplies(stones: string[]): boolean {
  return stones.length === 0 || stones.includes("DIAMOND");
}

function toNum(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Encode a numeric range the way products-api parses it: `"min-max"`, split on `"-"`.
 *
 * The separator makes some intents inexpressible, so they're normalised to the nearest honest
 * equivalent rather than sent as something the API will mangle:
 * - max-only would be `"-2"` → splits to `["", "2"]` → `parseFloat("")` is NaN → the whole filter is
 *   dropped. Sent as `"0-2"`, which means the same thing.
 * - a negative min breaks the split the same way, so bounds are clamped at 0 (neither carat nor
 *   price is ever negative).
 * - a max of 0 can't be sent at all: the where-builder guards with `filter.carat[1] && …`, so a
 *   falsy 0 drops the upper bound and silently turns the range into min-only. Dropped here instead.
 * - inverted bounds are swapped, so a typo narrows rather than returning nothing.
 */
export function rangeParam(min: string, max: string): string | null {
  let lo = toNum(min);
  let hi = toNum(max);
  if (lo !== null) lo = Math.max(0, lo);
  if (hi !== null) hi = Math.max(0, hi);
  if (hi !== null && hi <= 0) hi = null; // unsendable — see above
  if (lo === null && hi === null) return null;
  if (lo !== null && hi !== null) {
    const [a, b] = lo <= hi ? [lo, hi] : [hi, lo];
    return `${a}-${b}`;
  }
  if (lo !== null) return `${lo}`; // a lone value is a minimum
  return `0-${hi}`;
}

/** URLSearchParams percent-encodes commas; products-api wants them literal and a comma is legal
 *  unencoded in a query. Decoding them back keeps the copyable readout identical to the request. */
function toQueryString(params: URLSearchParams): string {
  return params.toString().replace(/%2C/g, ",");
}

export interface PageOptions {
  page: number;
  limit: number;
}

/** Build the exact `/combined-product` query string for these filters. */
export function buildQuery(kind: SpecimenKind, w: WireFilters, page: PageOptions): string {
  const p = new URLSearchParams();
  const csv = (key: string, values: string[]): void => {
    if (values.length > 0) p.set(key, values.join(","));
  };

  // ONE comma-separated value — never repeated params (trap 1).
  p.set("productType", kind === "stone" ? "GEMSTONE" : (w.productTypes.length > 0 ? w.productTypes : [...RING_PRODUCT_TYPES]).join(","));

  if (w.search.trim()) p.set("search", w.search.trim());
  csv("cut", w.cut);
  csv("colours", w.colours);
  csv("audience", w.audience);

  if (kind === "ring") {
    csv("defaultMetal", w.defaultMetal); // AND'd — never `metal` (trap 3)
    if (w.centreStone) p.set("centerStone", w.centreStone);
  } else {
    csv("stones", w.stones);
    if (w.includeNivoda) p.set("includeNivoda", "true");
    if (w.diamondOrigin && diamondOriginApplies(w.stones)) p.set("diamondOrigin", w.diamondOrigin);
  }

  const carat = rangeParam(w.caratMin, w.caratMax);
  if (carat) p.set("carat", carat);
  const price = rangeParam(w.priceMin, w.priceMax);
  if (price) p.set("price", price);

  if (w.stockStatus && w.stockStatus !== "ALL") p.set("stockStatus", w.stockStatus);
  if (w.withImages) p.set("productsWithImages", "true");

  p.set("page", String(page.page));
  p.set("limit", String(page.limit));
  return toQueryString(p);
}

/** The request as a tester would run it — the drawer shows this live and copies it verbatim. */
export function queryLine(kind: SpecimenKind, w: WireFilters, page: PageOptions): string {
  return `GET /combined-product?${buildQuery(kind, w, page)}`;
}

/** How many wire filters are narrowing the catalogue right now (search excluded — it has its own box). */
export function countWire(kind: SpecimenKind, w: WireFilters): number {
  let n = 0;
  if (w.productTypes.length > 0) n++;
  if (w.cut.length > 0) n++;
  if (w.colours.length > 0) n++;
  if (w.audience.length > 0) n++;
  if (kind === "ring") {
    if (w.defaultMetal.length > 0) n++;
    if (w.centreStone) n++;
  } else {
    if (w.stones.length > 0) n++;
    if (w.diamondOrigin && diamondOriginApplies(w.stones)) n++;
    // Nivoda is merged in by default, so turning it OFF is the active filter — and it has to count,
    // or a tester who narrows to own stock gets no "Clear" button and no way back.
    if (!w.includeNivoda) n++;
  }
  if (rangeParam(w.caratMin, w.caratMax)) n++;
  if (rangeParam(w.priceMin, w.priceMax)) n++;
  if (w.stockStatus && w.stockStatus !== "ALL") n++;
  if (w.withImages) n++;
  return n;
}

export function countSift(kind: SpecimenKind, s: SiftFilters): number {
  let n = 0;
  if (s.clarity.length > 0) n++;
  if (s.colourGrade.length > 0) n++;
  if (s.labGrown) n++;
  if (s.source.length > 0) n++;
  if (s.certLab.length > 0) n++;
  if (s.certified) n++;
  if (kind === "ring") {
    if (s.photos) n++;
    if (s.baseline) n++;
  }
  return n;
}

/** Does this stone satisfy every active stone-attribute sift? An absent attribute never matches an
 *  active filter — asking for VS1 shouldn't surface stones whose clarity was never recorded. */
function stoneMatches(stone: Stone | undefined, s: SiftFilters): boolean {
  if (s.clarity.length > 0 && !(stone?.clarity && s.clarity.includes(stone.clarity))) return false;
  if (s.colourGrade.length > 0 && !(stone?.colourGrade && s.colourGrade.includes(stone.colourGrade))) return false;
  if (s.source.length > 0 && !(stone?.source && s.source.includes(stone.source))) return false;
  if (s.certLab.length > 0 && !(stone?.certificationLab && s.certLab.includes(stone.certificationLab))) return false;
  if (s.labGrown === "lab" && stone?.labGrown !== true) return false;
  if (s.labGrown === "natural" && stone?.labGrown !== false) return false;
  if (s.certified === "yes" && stone?.certified !== true) return false;
  if (s.certified === "no" && stone?.certified !== false) return false;
  return true;
}

export function siftStones(rows: GemstoneSummary[], s: SiftFilters): GemstoneSummary[] {
  return rows.filter((g) => stoneMatches(g.stone, s));
}

/** Sift rings on ANY centre stone, not just the baseline — "has a VS1 centre stone" is the more
 *  useful reading for a two-stone ring, and the baseline sift below covers the other question. */
export function siftRings(rows: RingDetail[], s: SiftFilters): RingDetail[] {
  return rows.filter((r) => {
    if (s.photos === "multi" && r.images.length < 2) return false;
    if (s.baseline === "clean" && (!r.baselineReliable || r.warnings.length > 0)) return false;
    if (s.baseline === "flagged" && r.baselineReliable && r.warnings.length === 0) return false;
    // Counted as "stone" so the ring-only sifts above are excluded — a ring with no centre stone
    // should survive a photos/baseline sift, and only drop out once a stone attribute is asked for.
    if (countSift("stone", s) === 0) return true;
    return r.centreStones.some((st) => stoneMatches(st, s));
  });
}
